import { useEffect, useMemo, useState } from "react";
import { ColDef, ColGroupDef, ModelUpdatedEvent } from "ag-grid-community";

import useAppStoreContext from "../../../../shared/context/app-store/app-store.context";
import useHelpers from "../../../../shared/services/helpers/useHelpers.hook";
import IReportAvailableColumn from "../../../../shared/contracts/IReportAvailableColumn";



/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/10/2025
 * @remarks Please discuss before making any changes.!!!
 */
export default function useReportDataService() {

  const { DIFFERENCE } = useHelpers();
  const [colDefs, setColDefs] = useState<ColDef<any>[]>([]);
  const {
    selReportDetails,
    selReportData, currentAvailableColumns: availableCols,
    defaultPivotSelections: pivotCols
  } = useAppStoreContext();

  const styleInfo = selReportDetails?.styleInformation;

  useEffect(() => {
    // Dynamically set CSS variables based on wizardData
    if (styleInfo) {
      document.documentElement.style.setProperty('--header-text-color', styleInfo.headerForegroundColor || null);
      document.documentElement.style.setProperty('--header-background-color', styleInfo.headerColor || null);
      document.documentElement.style.setProperty('--header-border-color', styleInfo.borderColor || null);
    }
  }, [styleInfo]);

  useEffect(() => {
    setColDefs([]);
    if (selReportData?.length) {
      const keys = Object.keys(selReportData[0]);
      setColDefs([
        ...getFlatColDefs("", keys.filter(key => !key.includes("___"))),
        ...getNestedColDefs(keys.filter(key => key.includes("___")))
      ]);
    }
  }, [selReportData])

  const onModelUpdated = (params: ModelUpdatedEvent) => {
    params.api.autoSizeAllColumns()
  };

  const getFlatColDefs = (parentKey: string, flatColKeys: string[]): ColDef<any>[] => {

    const mappedCols = availableCols.filter(x => flatColKeys.includes(x.columnName));

    if (mappedCols.length !== flatColKeys.length) {
      console.warn("Some columns are missing",
        DIFFERENCE(flatColKeys, mappedCols.map(x => x.columnName))
      );
    }

    // ---
    return mappedCols.map(availableCol => ({
      field: (parentKey + availableCol.columnName),
      headerName: getColDescription(availableCol),
      editable: false, cellClass: "cell-with-separator",
      headerClass: "dynamic-header-styles",
      cellStyle: {
        borderTop: `1px solid ${styleInfo?.borderColor || null}`,
        borderLeft: `1px solid ${styleInfo?.borderColor || null}`,
      }
    }));
  };

  const getColDescription = (availableCol: IReportAvailableColumn) => {
    const pivotCol = pivotCols.find(x => x.columnName === availableCol.columnName);
    return pivotCol ? (pivotCol.columnDescription || pivotCol.columnName) :
      (availableCol.columnDescription || availableCol.columnName);
  }

  const getNestedColDefs = (nestedColKeys: string[]): ColGroupDef<any>[] => {
    // Group parent and child columns
    const parentChildrenMap = nestedColKeys.reduce((map, key) => {
      const [prefix, suffix] = key.split('___');
      map.set(prefix, [...(map.get(prefix) || []), suffix]);
      return map;
    }, new Map<string, string[]>());

    // Build nested column definitions. Dev-Tip: -> Array.from(targetIterable, mappingFnCallback)
    return Array.from(parentChildrenMap, ([parentKey, childKeys]) => ({
      headerName: parentKey,
      headerClass: "dynamic-header-styles",
      children: getFlatColDefs(`${parentKey}___`, childKeys),
    }));
  };

  const rowStyles = useMemo(() => {
    return (params: any) => {
      if (params.node.rowIndex % 2 === 0) {
        // Alternate row style
        return {
          ...(styleInfo?.itemColor?.startsWith('#') && { backgroundColor: styleInfo.itemColor }),
          ...(styleInfo?.itemForegroundColor?.startsWith('#') && { color: styleInfo.itemForegroundColor }),
        };
      }
      // Default row style
      return {
        ...(styleInfo?.alternatingItemColor?.startsWith('#') && { backgroundColor: styleInfo.alternatingItemColor }),
        ...(styleInfo?.alternatingItemForegroundColor?.startsWith('#') && { color: styleInfo.alternatingItemForegroundColor }),
      };
    };
  }, [styleInfo]);


  // ---
  return {
    colDefs, onModelUpdated,
    rowStyles, styleInfo,
  }
}